import { sum } from "lodash";

import { Layer } from "@/Utils/hierarchy";

import { AggregatedData } from "../types";

export const createTotalRow = (mergedView: Record<string, AggregatedData>): AggregatedData => {
  // Calculate totals from the actual data rows
  const rows = Object.values(mergedView);

  const totalSpend = {
    [Layer.Brand]: sum(rows.map((row) => row.spend[Layer.Brand] || 0)),
    [Layer.Category]: sum(rows.map((row) => row.spend[Layer.Category] || 0)),
    [Layer.Product]: sum(rows.map((row) => row.spend[Layer.Product] || 0)),
    total: sum(rows.map((row) => row.spend.total || 0)),
  };

  const totalRevenue = {
    [Layer.Brand]: sum(rows.map((row) => row.revenue[Layer.Brand] || 0)),
    [Layer.Category]: sum(rows.map((row) => row.revenue[Layer.Category] || 0)),
    [Layer.Product]: sum(rows.map((row) => row.revenue[Layer.Product] || 0)),
    total: sum(rows.map((row) => row.revenue.total || 0)),
    upperFunnelMarketingContribution: sum(
      rows.map((row) => row.revenue.upperFunnelMarketingContribution || 0)
    ),
    midFunnelMarketingContribution: sum(
      rows.map((row) => row.revenue.midFunnelMarketingContribution || 0)
    ),
  };

  // Calculate ROI for each layer
  const calculateROI = (spend: number, revenue: number) => {
    return spend > 0 ? ((revenue - spend) / spend) * 100 : 0;
  };

  const totalROI = {
    [Layer.Brand]: calculateROI(totalSpend[Layer.Brand], totalRevenue[Layer.Brand]),
    [Layer.Category]: calculateROI(totalSpend[Layer.Category], totalRevenue[Layer.Category]),
    [Layer.Product]: calculateROI(totalSpend[Layer.Product], totalRevenue[Layer.Product]),
    total: calculateROI(totalSpend.total, totalRevenue.total),
  };

  // Calculate ROAS (Return on Ad Spend) - same as ROI but expressed as a ratio
  const totalROAS = {
    [Layer.Brand]:
      totalSpend[Layer.Brand] > 0 ? (totalRevenue[Layer.Brand] / totalSpend[Layer.Brand]) * 100 : 0,
    [Layer.Category]:
      totalSpend[Layer.Category] > 0
        ? (totalRevenue[Layer.Category] / totalSpend[Layer.Category]) * 100
        : 0,
    [Layer.Product]:
      totalSpend[Layer.Product] > 0
        ? (totalRevenue[Layer.Product] / totalSpend[Layer.Product]) * 100
        : 0,
    total: totalSpend.total > 0 ? (totalRevenue.total / totalSpend.total) * 100 : 0,
  };

  // Calculate yield (revenue / spend) to match insights table calculation - this is actually ROAS
  const totalYield = {
    [Layer.Brand]:
      totalSpend[Layer.Brand] > 0 ? totalRevenue[Layer.Brand] / totalSpend[Layer.Brand] : 0,
    [Layer.Category]:
      totalSpend[Layer.Category] > 0
        ? totalRevenue[Layer.Category] / totalSpend[Layer.Category]
        : 0,
    [Layer.Product]:
      totalSpend[Layer.Product] > 0 ? totalRevenue[Layer.Product] / totalSpend[Layer.Product] : 0,
    total: totalSpend.total > 0 ? totalRevenue.total / totalSpend.total : 0,
  };

  // Calculate percentages based on the actual totals
  const totalSpendPercentage = {
    [Layer.Brand]: totalSpend.total > 0 ? (totalSpend[Layer.Brand] / totalSpend.total) * 100 : 0,
    [Layer.Category]:
      totalSpend.total > 0 ? (totalSpend[Layer.Category] / totalSpend.total) * 100 : 0,
    [Layer.Product]:
      totalSpend.total > 0 ? (totalSpend[Layer.Product] / totalSpend.total) * 100 : 0,
    total: 100,
  };

  const totalRevenuePercentage = {
    [Layer.Brand]:
      totalRevenue.total > 0 ? (totalRevenue[Layer.Brand] / totalRevenue.total) * 100 : 0,
    [Layer.Category]:
      totalRevenue.total > 0 ? (totalRevenue[Layer.Category] / totalRevenue.total) * 100 : 0,
    [Layer.Product]:
      totalRevenue.total > 0 ? (totalRevenue[Layer.Product] / totalRevenue.total) * 100 : 0,
    total: 100,
    upperFunnelMarketingContribution:
      totalRevenue.total > 0
        ? (totalRevenue.upperFunnelMarketingContribution / totalRevenue.total) * 100
        : 0,
    midFunnelMarketingContribution:
      totalRevenue.total > 0
        ? (totalRevenue.midFunnelMarketingContribution / totalRevenue.total) * 100
        : 0,
  };

  // Calculate total units
  const totalUnits = {
    [Layer.Brand]: sum(rows.map((row) => row.units?.[Layer.Brand] || 0)),
    [Layer.Category]: sum(rows.map((row) => row.units?.[Layer.Category] || 0)),
    [Layer.Product]: sum(rows.map((row) => row.units?.[Layer.Product] || 0)),
    total: sum(rows.map((row) => row.units?.total || 0)),
    upperFunnelMarketingContribution: sum(
      rows.map((row) => row.units?.upperFunnelMarketingContribution || 0)
    ),
    midFunnelMarketingContribution: sum(
      rows.map((row) => row.units?.midFunnelMarketingContribution || 0)
    ),
  };

  return {
    id: "total-row",
    mappedName: "Total",
    channel: "Total",
    geography: "",
    brand: "",
    category: "",
    product: "",
    sampleSize: sum(rows.map((row) => row.sampleSize || 0)),
    // Set adstock values to 0 so they display as N/A in the grid
    adstockPeakTotal: 0,
    adstockDurationTotal: 0,
    unit: sum(rows.map((row) => row.unit || 0)),
    adstockPeak: 0,
    adstockDuration: 0,
    units: totalUnits,
    yield: totalYield,
    spendPercentage: totalSpendPercentage,
    revenuePercentage: totalRevenuePercentage,
    roi: totalROI,
    roas: totalROAS,
    contribution: {
      [Layer.Brand]: sum(rows.map((row) => row.contribution?.[Layer.Brand] || 0)),
      [Layer.Category]: sum(rows.map((row) => row.contribution?.[Layer.Category] || 0)),
      [Layer.Product]: sum(rows.map((row) => row.contribution?.[Layer.Product] || 0)),
      total: sum(rows.map((row) => row.contribution?.total || 0)),
    },
    revenue: totalRevenue,
    spend: totalSpend,
  };
};
